import type { PageContent, TextItem } from "./pdfPageContent";

/**
 * Layout analysis for PDF -> Word / Excel: turns the loose pieces of text on a page into rows,
 * columns, tables and paragraphs. Pure functions (no pdf.js, no DOM), so every rule below is
 * covered by unit tests that run on real pdf.js output.
 *
 * Order of work on a page:
 *   1. items -> rows (text on one baseline; tolerance follows the font size)
 *   2. rows -> segments (words that belong together; a wide gap starts a new segment)
 *   3. a vertical gutter running through the page splits it into columns (read top to bottom, left to right)
 *   4. inside a column, runs of rows whose segments line up (by left, right or centre edge) are tables
 *   5. everything else becomes paragraphs (a gap, a size change, a short line or a list marker ends one)
 */

export interface Run {
  text: string;
  bold: boolean;
  italic: boolean;
  sizePt: number;
  /** "RRGGBB", undefined = default colour. */
  color?: string;
  font?: string;
  link?: string;
  /** A Word field in place of literal text (used for page numbers in headers and footers). */
  field?: "PAGE" | "NUMPAGES";
}

export type Align = "left" | "center" | "right";

export interface ParagraphBlock {
  kind: "paragraph";
  runs: Run[];
  topPt: number;
  bottomPt: number;
  leftPt: number;
  rightPt: number;
  align?: Align;
  /** Left indent in points (0 for none). */
  indentPt: number;
  bullet?: { level: number };
  /** Space to leave below, in points. */
  spaceAfterPt: number;
  sizePt: number;
}

export interface TableCell {
  runs: Run[];
}

export interface TableBlock {
  kind: "table";
  rows: TableCell[][];
  colWidthsPt: number[];
  colAlign: Align[];
  /** Where each column's text really sits (leftmost and rightmost edge), in points. */
  colExtents?: { left: number; right: number }[];
  topPt: number;
  bottomPt: number;
  leftPt: number;
  rightPt: number;
}

export type LayoutBlock = ParagraphBlock | TableBlock;

// --------------------------------------------------------------------------------------------
// 1-2. rows and segments
// --------------------------------------------------------------------------------------------

export interface Seg {
  left: number;
  right: number;
  items: TextItem[];
  size: number;
  text: string;
}

export interface Row {
  baseline: number;
  size: number;
  top: number;
  segs: Seg[];
  /** A bullet / number marker taken off the front of the row. */
  marker?: { kind: "bullet" | "number"; text: string; left: number };
}

const BULLET_ONLY = /^[•●▪◦‣·■▫⁃∙-*\-–—]$/;
const NUMBER_ONLY = /^(\d{1,3}[.)]|\(\d{1,3}\)|[a-zA-Z][.)]|\([a-zA-Z]\)|[ivxIVX]{1,5}[.)])$/;
const BULLET_PREFIX = /^([•●▪◦‣·■▫⁃∙-*]|[-–—])\s+(?=\S)/;
const NUMBER_PREFIX = /^(\d{1,3}[.)]|\(\d{1,3}\)|[a-z][.)]|\([a-z]\))\s+(?=\S)/;

const segGapFor = (size: number) => Math.max(8, 0.8 * size);
const spaceGapFor = (size: number) => 0.15 * size;

function textOfItems(items: TextItem[]): string {
  let out = "";
  let right = -Infinity;
  for (const it of items) {
    const t = it.str.replace(/\s+/g, " ");
    if (out && right > -Infinity) {
      const gap = it.x - right;
      if (gap > spaceGapFor(it.size) && !/\s$/.test(out) && !/^\s/.test(t)) out += " ";
    }
    out += t;
    right = Math.max(right, it.x + it.width);
  }
  return out.trim();
}

function makeSeg(items: TextItem[]): Seg {
  const sorted = [...items].sort((a, b) => a.x - b.x);
  return {
    left: Math.min(...sorted.map((i) => i.x)),
    right: Math.max(...sorted.map((i) => i.x + i.width)),
    items: sorted,
    size: Math.max(...sorted.map((i) => i.size)),
    text: textOfItems(sorted),
  };
}

function rowFromItems(items: TextItem[]): Row {
  const sorted = [...items].sort((a, b) => a.x - b.x);
  const groups: TextItem[][] = [];
  let cur: TextItem[] = [];
  let right = -Infinity;
  for (const it of sorted) {
    if (cur.length && it.x - right > segGapFor(Math.max(it.size, cur[cur.length - 1].size))) {
      groups.push(cur);
      cur = [];
      right = -Infinity;
    }
    cur.push(it);
    right = Math.max(right, it.x + it.width);
  }
  if (cur.length) groups.push(cur);
  const segs = groups.map(makeSeg).filter((s) => s.text.length > 0);
  const biggest = items.reduce((a, b) => (b.size > a.size ? b : a), items[0]);
  return { baseline: biggest.baseline, size: biggest.size, top: biggest.baseline - biggest.size * 0.8, segs };
}

/** Groups text items into rows (items on one baseline). Tolerance follows the font size so superscripts stay on their line but neighbouring lines never merge. */
export function buildRows(items: TextItem[]): Row[] {
  const usable = items.filter((i) => i.str.trim().length > 0);
  const normal = usable.filter((i) => !i.rotated).sort((a, b) => a.baseline - b.baseline || a.x - b.x);
  const groups: { ref: number; size: number; items: TextItem[] }[] = [];
  for (const it of normal) {
    const g = groups[groups.length - 1];
    const tol = Math.min(5, Math.max(2, 0.35 * Math.max(it.size, g?.size ?? 0)));
    if (g && Math.abs(it.baseline - g.ref) <= tol) {
      g.items.push(it);
      g.size = Math.max(g.size, it.size);
    } else groups.push({ ref: it.baseline, size: it.size, items: [it] });
  }
  const rows = groups.map((g) => rowFromItems(g.items));
  // Text that is not horizontal is kept, one item per row, so it is never lost.
  for (const it of usable.filter((i) => i.rotated)) rows.push(rowFromItems([it]));
  rows.sort((a, b) => a.baseline - b.baseline);
  for (const r of rows) stripMarker(r);
  return rows.filter((r) => r.segs.length > 0 || r.marker);
}

function stripMarker(row: Row): void {
  const first = row.segs[0];
  if (!first) return;
  if (row.segs.length >= 2) {
    if (BULLET_ONLY.test(first.text)) {
      row.marker = { kind: "bullet", text: first.text, left: first.left };
      row.segs.shift();
      return;
    }
    if (NUMBER_ONLY.test(first.text)) {
      row.marker = { kind: "number", text: first.text, left: first.left };
      row.segs.shift();
      return;
    }
  } else if (row.segs.length === 1 && BULLET_ONLY.test(first.text) && first.text.length === 1 && /[•●▪◦‣⁃∙]/.test(first.text)) {
    // A bare bullet glyph with its text on the next line of the content stream: keep it as marker with no text.
    row.marker = { kind: "bullet", text: first.text, left: first.left };
    row.segs = [];
  }
}

const rowLeft = (r: Row) => (r.segs.length ? Math.min(...r.segs.map((s) => s.left)) : (r.marker?.left ?? 0));
const rowRight = (r: Row) => (r.segs.length ? Math.max(...r.segs.map((s) => s.right)) : (r.marker?.left ?? 0));
export const rowText = (r: Row) => r.segs.map((s) => s.text).join(" ");

// --------------------------------------------------------------------------------------------
// 3. columns
// --------------------------------------------------------------------------------------------

export interface Gutter {
  left: number;
  right: number;
}

const LONG_SEG_CHARS = 20;

/** A vertical gap that runs down the page between two blocks of running text, or null. Exported for tests. */
export function findGutter(rows: Row[]): Gutter | null {
  const withSegs = rows.filter((r) => r.segs.length > 0);
  if (withSegs.length < 6) return null;
  const minL = Math.min(...withSegs.map(rowLeft));
  const maxR = Math.max(...withSegs.map(rowRight));
  const W = maxR - minL;
  if (W < 220) return null;

  const from = Math.ceil(minL + 0.18 * W);
  const to = Math.floor(maxR - 0.18 * W);
  type Cand = { x: number; cross: number; L: number; R: number };
  const cands: Cand[] = [];
  for (let x = from; x <= to; x += 2) {
    let cross = 0, L = 0, R = 0;
    for (const r of withSegs) {
      let crosses = false, hasL = false, hasR = false;
      for (const s of r.segs) {
        if (s.left + 1 < x && s.right - 1 > x) crosses = true;
        else if (s.right <= x + 1 && s.text.length >= LONG_SEG_CHARS) hasL = true;
        else if (s.left >= x - 1 && s.text.length >= LONG_SEG_CHARS) hasR = true;
      }
      if (crosses) cross++;
      if (hasL) L++;
      if (hasR) R++;
    }
    cands.push({ x, cross, L, R });
  }
  const scored = cands
    .map((c) => ({ ...c, side: Math.min(c.L, c.R) }))
    .filter((c) => c.side >= 3 && c.cross <= 0.5 * c.side);
  if (!scored.length) return null;
  // Best: most text on both sides, fewest lines running through.
  scored.sort((a, b) => b.side - 2 * b.cross - (a.side - 2 * a.cross));
  const best = scored[0];
  // Grow to the full empty interval around it.
  const crossAt = (x: number) => {
    let n = 0;
    for (const r of withSegs) if (r.segs.some((s) => s.left + 1 < x && s.right - 1 > x)) n++;
    return n;
  };
  let gl = best.x, gr = best.x;
  while (gl - 1 > minL && crossAt(gl - 1) <= best.cross) gl--;
  while (gr + 1 < maxR && crossAt(gr + 1) <= best.cross) gr++;
  if (gr - gl < 10) return null;
  // Tighten to the real text edges either side, so a column's first character is never inside the gutter.
  const mid = (gl + gr) / 2;
  const allSegs = withSegs.flatMap((r) => r.segs);
  const lefts = allSegs.filter((s) => s.right <= mid + 1).map((s) => s.right);
  const rights = allSegs.filter((s) => s.left >= mid - 1).map((s) => s.left);
  const tl = lefts.length ? Math.max(...lefts.filter((v) => v <= gr)) : gl;
  const tr = rights.length ? Math.min(...rights.filter((v) => v >= gl)) : gr;
  if (!Number.isFinite(tl) || !Number.isFinite(tr) || tr - tl < 10) return null;
  return { left: tl, right: tr };
}

const crossesGutter = (s: Seg, g: Gutter) => s.left < g.right - 0.01 && s.right > g.left + 0.01;

/** The part of a row that falls in one column: size and baseline follow the text that stayed. */
function subRow(r: Row, segs: Seg[], marker: Row["marker"]): Row {
  const items = segs.flatMap((sg) => sg.items);
  const big = items.reduce((a, b) => (b.size > a.size ? b : a), items[0]);
  return { baseline: big.baseline, size: big.size, top: big.baseline - big.size * 0.8, segs, marker };
}

/** Splits rows into reading-order regions: full-width rows, then the left column of a band, then its right column. */
export function orderRegions(rows: Row[], depth = 0): Row[][] {
  const gutter = depth < 2 ? findGutter(rows) : null;
  if (!gutter) return [rows];
  const out: Row[][] = [];
  let full: Row[] = [];
  let left: Row[] = [];
  let right: Row[] = [];
  const flushBand = () => {
    if (left.length) out.push(...orderRegions(left, depth + 1));
    if (right.length) out.push(...orderRegions(right, depth + 1));
    left = [];
    right = [];
  };
  const flushFull = () => {
    if (full.length) out.push(full);
    full = [];
  };
  for (const r of rows) {
    if (r.segs.some((s) => crossesGutter(s, gutter))) {
      flushBand();
      full.push(r);
      continue;
    }
    flushFull();
    const l = r.segs.filter((s) => s.right <= gutter.left + 0.01);
    const rr = r.segs.filter((s) => s.left >= gutter.right - 0.01);
    // A marker belongs to the side its row's first segment is on.
    if (l.length) left.push(subRow(r, l, r.marker && r.marker.left <= gutter.left ? r.marker : undefined));
    else if (r.marker && r.marker.left <= gutter.left && !rr.length) left.push({ ...r, segs: [] });
    if (rr.length) right.push(subRow(r, rr, r.marker && r.marker.left > gutter.left ? r.marker : undefined));
  }
  flushBand();
  flushFull();
  return out.filter((reg) => reg.length > 0);
}

// --------------------------------------------------------------------------------------------
// 4. tables
// --------------------------------------------------------------------------------------------

interface TableColumn {
  left: number;
  right: number;
  members: { row: number; seg: Seg }[];
  align: Align;
}

class Dsu {
  p: number[];
  constructor(n: number) {
    this.p = Array.from({ length: n }, (_, i) => i);
  }
  find(i: number): number {
    while (this.p[i] !== i) {
      this.p[i] = this.p[this.p[i]];
      i = this.p[i];
    }
    return i;
  }
  union(a: number, b: number) {
    this.p[this.find(a)] = this.find(b);
  }
}

function clusterBy(values: number[], tol: number): number[][] {
  const idx = values.map((v, i) => [v, i] as const).sort((a, b) => a[0] - b[0]);
  const out: number[][] = [];
  let cur: number[] = [];
  let last = -Infinity;
  for (const [v, i] of idx) {
    if (cur.length && v - last > tol) {
      out.push(cur);
      cur = [];
    }
    cur.push(i);
    last = v;
  }
  if (cur.length) out.push(cur);
  return out;
}

/**
 * Finds the columns of a run of rows. Segments that share a left edge, a right edge or a centre are
 * one column; so a right-aligned number column is one column even though its entries start at
 * different x positions (the cause of the old "amounts spread over two columns" bug). A heading
 * that lines up with nothing joins the column it overlaps, or the nearest free one.
 */
export function buildColumns(rows: Row[]): { columns: TableColumn[]; cellOf: Map<Seg, number> } {
  const segs: { row: number; seg: Seg }[] = [];
  rows.forEach((r, ri) => r.segs.forEach((s) => segs.push({ row: ri, seg: s })));
  const dsu = new Dsu(segs.length);
  for (const g of clusterBy(segs.map((s) => s.seg.left), 2.5)) for (let k = 1; k < g.length; k++) if (g.length >= 2) dsu.union(g[0], g[k]);
  for (const g of clusterBy(segs.map((s) => s.seg.right), 2.5)) for (let k = 1; k < g.length; k++) if (g.length >= 2) dsu.union(g[0], g[k]);
  for (const g of clusterBy(segs.map((s) => (s.seg.left + s.seg.right) / 2), 2)) if (g.length >= 3) for (let k = 1; k < g.length; k++) dsu.union(g[0], g[k]);

  const comps = new Map<number, number[]>();
  segs.forEach((_, i) => {
    const r = dsu.find(i);
    (comps.get(r) ?? comps.set(r, []).get(r)!).push(i);
  });
  const solid: number[][] = [];
  const orphans: number[] = [];
  for (const members of Array.from(comps.values())) {
    if (members.length >= 2) solid.push(members);
    else orphans.push(members[0]);
  }

  const columns: TableColumn[] = [];
  const colOfSeg = new Map<number, number>();
  const mk = (members: number[]): TableColumn => ({
    left: Math.min(...members.map((i) => segs[i].seg.left)),
    right: Math.max(...members.map((i) => segs[i].seg.right)),
    members: members.map((i) => ({ row: segs[i].row, seg: segs[i].seg })),
    align: "left",
  });
  for (const m of solid) {
    const c = columns.length;
    columns.push(mk(m));
    m.forEach((i) => colOfSeg.set(i, c));
  }
  const rowHas = (ci: number, ri: number) => columns[ci].members.some((m) => m.row === ri);
  for (const i of orphans) {
    const { row, seg } = segs[i];
    let best = -1, bestScore = Infinity;
    columns.forEach((c, ci) => {
      if (rowHas(ci, row)) return;
      const overlap = Math.min(seg.right, c.right) - Math.max(seg.left, c.left);
      const dist = overlap > 0 ? -overlap : Math.max(seg.left - c.right, c.left - seg.right);
      if (dist < bestScore) {
        bestScore = dist;
        best = ci;
      }
    });
    if (best >= 0 && bestScore < 40) {
      columns[best].members.push({ row, seg });
      columns[best].left = Math.min(columns[best].left, seg.left);
      columns[best].right = Math.max(columns[best].right, seg.right);
      colOfSeg.set(i, best);
    } else {
      colOfSeg.set(i, columns.length);
      columns.push(mk([i]));
    }
  }

  // Order columns left to right and remember each cell's column.
  const order = columns.map((c, i) => [i, (c.left + c.right) / 2] as const).sort((a, b) => a[1] - b[1]).map((x) => x[0]);
  const rank = new Map<number, number>();
  order.forEach((ci, r) => rank.set(ci, r));
  const sorted = order.map((ci) => columns[ci]);
  const cellOf = new Map<Seg, number>();
  segs.forEach((s, i) => cellOf.set(s.seg, rank.get(colOfSeg.get(i)!)!));

  // Alignment of each column from how its entries line up.
  for (const c of sorted) {
    const lefts = c.members.map((m) => m.seg.left);
    const rights = c.members.map((m) => m.seg.right);
    const spread = (v: number[]) => Math.max(...v) - Math.min(...v);
    const leftTight = c.members.filter((m) => Math.abs(m.seg.left - median(lefts)) <= 2.5).length / c.members.length;
    const rightTight = c.members.filter((m) => Math.abs(m.seg.right - median(rights)) <= 2.5).length / c.members.length;
    if (rightTight >= 0.6 && leftTight < 0.6 && spread(lefts) > 4) c.align = "right";
    else if (leftTight >= 0.5) c.align = "left";
    else {
      const centers = c.members.map((m) => (m.seg.left + m.seg.right) / 2);
      const centerTight = centers.filter((v) => Math.abs(v - median(centers)) <= 3).length / centers.length;
      c.align = centerTight >= 0.6 ? "center" : "left";
    }
  }
  return { columns: sorted, cellOf };
}

function median(v: number[]): number {
  const s = [...v].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/** True when the columns really line up: most segments share an edge with at least one other row. */
function lineUpScore(rows: Row[]): number {
  const segs = rows.flatMap((r) => r.segs);
  if (!segs.length) return 0;
  const near = (a: number, b: number) => Math.abs(a - b) <= 2.5;
  let ok = 0;
  for (const s of segs) {
    const mate = segs.some((o) => o !== s && (near(o.left, s.left) || near(o.right, s.right) || near((o.left + o.right) / 2, (s.left + s.right) / 2)));
    if (mate) ok++;
  }
  return ok / segs.length;
}

// --------------------------------------------------------------------------------------------
// runs
// --------------------------------------------------------------------------------------------

const sameStyle = (a: Run, b: Run) =>
  a.bold === b.bold && a.italic === b.italic && Math.abs(a.sizePt - b.sizePt) < 0.6 && a.color === b.color && a.font === b.font && a.link === b.link && !a.field && !b.field;

function pushRun(runs: Run[], run: Run) {
  if (!run.text && !run.field) return;
  const last = runs[runs.length - 1];
  if (last && sameStyle(last, run)) last.text += run.text;
  else runs.push({ ...run });
}

/** Runs for the items of one segment: same-style neighbours merge, a space goes wherever the page had a gap. */
export function segRuns(seg: Seg): Run[] {
  const runs: Run[] = [];
  let right = -Infinity;
  let pending = false;
  for (const it of seg.items) {
    const t = it.str.replace(/\s+/g, " ");
    const gap = right > -Infinity ? it.x - right : 0;
    const lastText = runs.length ? runs[runs.length - 1].text : "";
    if (runs.length && gap > spaceGapFor(it.size) && !/\s$/.test(lastText) && !/^\s/.test(t)) pending = true;
    if (pending) {
      const last = runs[runs.length - 1];
      last.text += " ";
      pending = false;
    }
    pushRun(runs, { text: t, bold: it.bold, italic: it.italic, sizePt: round1(it.size), color: it.color, font: it.font, link: it.link });
    right = Math.max(right, it.x + it.width);
  }
  trimRuns(runs);
  return runs;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

function trimRuns(runs: Run[]) {
  while (runs.length && !runs[0].field && !runs[0].text.trim()) runs.shift();
  while (runs.length && !runs[runs.length - 1].field && !runs[runs.length - 1].text.trim()) runs.pop();
  if (runs.length && !runs[0].field) runs[0].text = runs[0].text.replace(/^\s+/, "");
  const last = runs[runs.length - 1];
  if (last && !last.field) last.text = last.text.replace(/\s+$/, "");
}

/** All segments of a row as one run list; segments far apart are separated by a tab, near ones by a space. */
function rowRuns(row: Row): Run[] {
  const runs: Run[] = [];
  row.segs.forEach((s, i) => {
    if (i > 0) {
      const gap = s.left - row.segs[i - 1].right;
      const sep = gap > 3 * row.size ? "\t" : " ";
      const last = runs[runs.length - 1];
      if (last) last.text += sep;
    }
    for (const r of segRuns(s)) pushRun(runs, r);
  });
  trimRuns(runs);
  return runs;
}

function cleanRunText(runs: Run[]): Run[] {
  for (const r of runs) r.text = r.text.replace(/ {2,}/g, " ");
  return runs;
}

// --------------------------------------------------------------------------------------------
// 5. paragraphs
// --------------------------------------------------------------------------------------------

interface Line {
  row: Row;
  runs: Run[];
  text: string;
  left: number;
  right: number;
  baseline: number;
  top: number;
  size: number;
  allBold: boolean;
  marker?: Row["marker"];
  /** A line made of separate parts (label ... value): stays a line of its own. */
  multi: boolean;
}

function makeLine(row: Row): Line | null {
  let runs = rowRuns(row);
  let marker = row.marker;
  // A marker typed into the text itself: "• Item", "1. Item", "- Item".
  if (!marker && runs.length) {
    const plain = runs.map((r) => r.text).join("");
    const b = BULLET_PREFIX.exec(plain);
    const n = !b ? NUMBER_PREFIX.exec(plain) : null;
    if (b) {
      marker = { kind: "bullet", text: b[1], left: rowLeft(row) };
      runs = stripPrefix(runs, b[0].length);
    } else if (n) {
      marker = { kind: "number", text: n[1], left: rowLeft(row) };
      // Numbered markers stay in the text: the numbers in a PDF are part of what it says.
    }
  }
  if (!runs.length && !marker) return null;
  const sized = runs.filter((r) => r.text.trim());
  const visible = sized.length ? sized : runs;
  const allBold = visible.length > 0 && visible.every((r) => r.bold);
  const text = runs.map((r) => r.text).join("");
  const left = marker && row.marker ? Math.min(rowLeft(row), marker.left) : rowLeft(row);
  return { row, runs: cleanRunText(runs), text, left, right: rowRight(row), baseline: row.baseline, top: row.top, size: row.size, allBold, marker, multi: row.segs.length >= 2 };
}

function stripPrefix(runs: Run[], n: number): Run[] {
  const out: Run[] = [];
  let left = n;
  for (const r of runs) {
    if (left <= 0) out.push(r);
    else if (r.text.length <= left) left -= r.text.length;
    else {
      out.push({ ...r, text: r.text.slice(left) });
      left = 0;
    }
  }
  return out;
}

const endsTerminal = (t: string) => /[.!?:;,)"”’]\s*$/.test(t);

/** Joins the lines of one paragraph. Lines meet with a space; a hyphen at the end of a line stays and no space is added after it. */
function joinLines(lines: Line[]): Run[] {
  const runs: Run[] = [];
  lines.forEach((ln, i) => {
    if (i > 0) {
      const prev = lines[i - 1].text;
      const hyphenated = /[A-Za-zÀ-ɏ]-$/.test(prev) && /^[a-zß-ÿ]/.test(ln.text);
      if (!hyphenated && runs.length) runs[runs.length - 1].text += " ";
    }
    for (const r of ln.runs) pushRun(runs, r);
  });
  trimRuns(runs);
  return cleanRunText(runs);
}

/** The usual gap between two lines of one paragraph at this size: the lower quartile of the page's line gaps, clamped to a sane range. */
function leadingFor(lines: Line[]): number {
  const gaps: number[] = [];
  for (let i = 1; i < lines.length; i++) {
    const a = lines[i - 1], b = lines[i];
    if (Math.abs(a.size - b.size) >= 0.6) continue;
    const g = b.baseline - a.baseline;
    if (g >= 0.9 * a.size && g <= 3 * a.size) gaps.push(g);
  }
  if (gaps.length < 3) return NaN;
  gaps.sort((x, y) => x - y);
  return gaps[Math.floor(0.25 * (gaps.length - 1))];
}

export function paragraphsFromRows(rows: Row[], bounds: { left: number; right: number }): ParagraphBlock[] {
  const lines = rows.map(makeLine).filter((l): l is Line => !!l);
  if (!lines.length) return [];
  const regionLeft = bounds.left;
  const regionRight = bounds.right;
  const lead = leadingFor(lines);
  const longRights = lines.filter((l) => l.text.length >= 25).map((l) => l.right);
  const colRight = longRights.length ? Math.max(...longRights) : regionRight;
  const shortBy = Math.max(30, 0.15 * (colRight - regionLeft));

  const groups: Line[][] = [];
  let cur: Line[] = [];
  const breakBetween = (prev: Line, ln: Line, groupLen: number): boolean => {
    if (ln.marker || ln.multi || prev.multi) return true;
    const gap = ln.baseline - prev.baseline;
    const size = Math.max(prev.size, ln.size);
    const base = Number.isFinite(lead) ? Math.min(lead, 1.7 * size) : 1.2 * size;
    if (gap > Math.max(1.6 * size, base * 1.4)) return true;
    if (Math.abs(prev.size - ln.size) > 1) return true;
    if (prev.allBold !== ln.allBold) return true;
    const prevIsListStart = !!cur[0]?.marker;
    if (ln.left > prev.left + 8 && !(prevIsListStart && Math.abs(ln.left - prev.left) < 40 && !ln.marker && groupLen === 1)) return true;
    if (ln.left < prev.left - 8 && groupLen > 1) return true;
    // A heading that wraps (same bold, same size, tight spacing, no full stop) is still one paragraph (PW-13).
    const wrappedHeading = prev.allBold && ln.allBold && !endsTerminal(prev.text) && gap <= 1.35 * size && /^[a-zß-ÿ0-9]/i.test(ln.text) && prev.right > regionLeft + 0.6 * (colRight - regionLeft);
    if (!wrappedHeading && prev.right < colRight - shortBy && (endsTerminal(prev.text) || !/^[a-zß-ÿ]/.test(ln.text))) return true;
    return false;
  };
  for (const ln of lines) {
    if (cur.length && breakBetween(cur[cur.length - 1], ln, cur.length)) {
      groups.push(cur);
      cur = [];
    }
    cur.push(ln);
  }
  if (cur.length) groups.push(cur);

  const out: ParagraphBlock[] = [];
  groups.forEach((g, gi) => {
    const first = g[0], last = g[g.length - 1];
    const runs = joinLines(g);
    if (!runs.length) return;
    const left = Math.min(...g.map((l) => l.left));
    const right = Math.max(...g.map((l) => l.right));
    const size = round1(Math.max(...g.map((l) => l.size)));
    let align: Align | undefined;
    const sideLeft = left - regionLeft;
    const sideRight = regionRight - right;
    if (g.length === 1 && !first.marker) {
      const centreOff = (left + right) / 2 - (regionLeft + regionRight) / 2;
      if (sideLeft > 12 && sideRight > 12 && Math.abs(centreOff) < 12 && right - left < 0.85 * (regionRight - regionLeft)) align = "center";
      else if (sideRight < 4 && sideLeft > 40) align = "right";
    }
    const next = groups[gi + 1]?.[0];
    const spaceAfter = next ? Math.max(0, Math.min(36, next.baseline - last.baseline - 1.2 * last.size)) : 0;
    const bullet = first.marker?.kind === "bullet" ? { level: Math.max(0, Math.min(2, Math.floor((first.marker.left - regionLeft) / 18))) } : undefined;
    out.push({
      kind: "paragraph",
      runs,
      topPt: first.top,
      bottomPt: last.baseline + last.size * 0.25,
      leftPt: left,
      rightPt: right,
      align,
      indentPt: !bullet && !align && sideLeft > 14 ? Math.round(sideLeft) : 0,
      bullet,
      spaceAfterPt: Math.round(spaceAfter * 10) / 10,
      sizePt: size,
    });
  });
  return out;
}

// --------------------------------------------------------------------------------------------
// regions -> blocks
// --------------------------------------------------------------------------------------------

const MIN_TABLE_ROWS = 3;

function tableFromRows(rows: Row[]): TableBlock | null {
  const { columns, cellOf } = buildColumns(rows);
  if (columns.length < 2) return null;
  const cellRuns = rows.map((r) => {
    const cells: Run[][] = columns.map(() => []);
    for (const s of r.segs) {
      const target = cells[cellOf.get(s)!];
      if (target.length) target[target.length - 1].text += " ";
      for (const run of segRuns(s)) pushRun(target, run);
    }
    cells.forEach((c) => trimRuns(c));
    return cells;
  });
  // Every column must carry something in at least two rows, or it is not a column.
  const filled = columns.map((_, ci) => cellRuns.filter((r) => r[ci].length > 0).length);
  if (filled.filter((n) => n >= 2).length < 2) return null;
  const bounds: number[] = [];
  columns.forEach((c, i) => {
    const start = i === 0 ? c.left - 3 : (columns[i - 1].right + c.left) / 2;
    const end = i === columns.length - 1 ? c.right + 3 : (c.right + columns[i + 1].left) / 2;
    bounds.push(Math.max(12, end - start));
  });
  const first = rows[0], last = rows[rows.length - 1];
  return {
    kind: "table",
    rows: cellRuns.map((r) => r.map((runs) => ({ runs: cleanRunText(runs) }))),
    colWidthsPt: bounds,
    colAlign: columns.map((c) => c.align),
    colExtents: columns.map((c) => ({ left: c.left, right: c.right })),
    topPt: first.top,
    bottomPt: last.baseline + last.size * 0.25,
    leftPt: Math.min(...columns.map((c) => c.left)),
    rightPt: Math.max(...columns.map((c) => c.right)),
  };
}

/** Two columns whose first column holds only 1, 2, 3 ... (or a, b, c ...): that is a list. */
export function isNumberedList(t: TableBlock): boolean {
  if (t.rows.length < 2 || t.rows[0].length !== 2) return false;
  const marks = t.rows.map((r) => r[0].runs.map((x) => x.text).join("").trim());
  if (!marks.every((m) => /^(\d{1,3}|[A-Za-z])[.)]?$/.test(m))) return false;
  const nums = marks.map((m) => parseInt(m, 10));
  if (nums.every((n) => !Number.isNaN(n))) return nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
  const codes = marks.map((m) => m.replace(/[.)]/g, "").toLowerCase().charCodeAt(0));
  return codes.every((c, i) => i === 0 || c === codes[i - 1] + 1);
}

/** Splits a region's rows into table runs and prose, in order, and builds the blocks. */
export function blocksFromRegion(rows: Row[], bounds: { left: number; right: number }): LayoutBlock[] {
  const blocks: LayoutBlock[] = [];
  let prose: Row[] = [];
  const flushProse = () => {
    if (prose.length) blocks.push(...paragraphsFromRows(prose, bounds));
    prose = [];
  };
  const isTabular = (r: Row) => r.segs.length >= 2 && !r.marker;

  let i = 0;
  while (i < rows.length) {
    if (!isTabular(rows[i])) {
      prose.push(rows[i]);
      i++;
      continue;
    }
    let j = i + 1;
    while (j < rows.length && isTabular(rows[j]) && rows[j].baseline - rows[j - 1].baseline <= 3.2 * Math.max(rows[j].size, rows[j - 1].size)) j++;
    const run = rows.slice(i, j);
    let table: TableBlock | null = null;
    if (run.length >= MIN_TABLE_ROWS && lineUpScore(run) >= 0.7) table = tableFromRows(run);
    if (table && isNumberedList(table)) table = null; // a short numbered list is a list, not a table (PW-14, PX-10)
    if (table) {
      flushProse();
      blocks.push(table);
    } else prose.push(...run);
    i = j;
  }
  flushProse();
  return blocks;
}

export function layoutRows(rows: Row[], pageBounds: { left: number; right: number }): LayoutBlock[] {
  const regions = orderRegions(rows);
  const out: LayoutBlock[] = [];
  for (const reg of regions) {
    const withSegs = reg.filter((r) => r.segs.length);
    if (!withSegs.length && !reg.some((r) => r.marker)) continue;
    const isFull = regions.length === 1 || reg.some((r) => r.segs.some((s) => s.right - s.left > 0.6 * (pageBounds.right - pageBounds.left)));
    const bounds = isFull
      ? pageBounds
      : { left: Math.min(...withSegs.map(rowLeft)), right: Math.max(...withSegs.map(rowRight)) };
    const blocks = blocksFromRegion(reg, bounds);
    // Leave a gap where one column ends and the next begins.
    const prev = out[out.length - 1];
    if (prev && prev.kind === "paragraph" && prev.spaceAfterPt < 10) prev.spaceAfterPt = 10;
    out.push(...blocks);
  }
  return out;
}

// --------------------------------------------------------------------------------------------
// whole document: headers, footers, margins
// --------------------------------------------------------------------------------------------

export interface HeaderFooterLine {
  /** One run list per segment (left / centre / right part). */
  parts: Run[][];
  align: Align;
}

export interface PageLayout {
  widthPt: number;
  heightPt: number;
  blocks: LayoutBlock[];
  marginsPt: { top: number; right: number; bottom: number; left: number };
  hasText: boolean;
}

export interface DocumentLayout {
  pages: PageLayout[];
  header: HeaderFooterLine[];
  footer: HeaderFooterLine[];
}

const digitGroups = (s: string) => s.match(/\d+/g) ?? [];
const normalizeDigits = (s: string) => s.replace(/\d+/g, "#");

interface HfCand {
  page: number;
  row: Row;
  zone: "header" | "footer";
  text: string;
}

/** Repeated lines at the top or bottom of the pages (running headers, page numbers). Returns the rows to take out of the body and the Word header / footer lines. */
export function detectHeadersFooters(pages: { rows: Row[]; heightPt: number }[], widthPt: number): {
  remove: Set<Row>;
  header: HeaderFooterLine[];
  footer: HeaderFooterLine[];
} {
  const remove = new Set<Row>();
  const empty = { remove, header: [] as HeaderFooterLine[], footer: [] as HeaderFooterLine[] };
  const n = pages.length;
  if (n < 2) return empty;

  // Body text size: the size with the most characters.
  const chars = new Map<number, number>();
  for (const p of pages) for (const r of p.rows) for (const s of r.segs) chars.set(Math.round(s.size * 2) / 2, (chars.get(Math.round(s.size * 2) / 2) ?? 0) + s.text.length);
  let bodySize = 11, best = 0;
  chars.forEach((c, sz) => {
    if (c > best) {
      best = c;
      bodySize = sz;
    }
  });

  const cands: HfCand[] = [];
  pages.forEach((p, pi) => {
    for (const r of p.rows) {
      if (!r.segs.length || r.segs.length > 3) continue;
      if (r.size > bodySize * 1.05 + 0.5) continue;
      const text = r.segs.map((s) => s.text).join(" ");
      if (r.top < 0.1 * p.heightPt) cands.push({ page: pi, row: r, zone: "header", text });
      else if (r.baseline > 0.9 * p.heightPt) cands.push({ page: pi, row: r, zone: "footer", text });
    }
  });

  const need = Math.max(2, Math.ceil(0.75 * n));
  const byKey = new Map<string, HfCand[]>();
  for (const c of cands) {
    const key = c.zone + "|" + c.row.segs.length + "|" + normalizeDigits(c.text);
    (byKey.get(key) ?? byKey.set(key, []).get(key)!).push(c);
  }

  const made: { zone: "header" | "footer"; baseline: number; line: HeaderFooterLine; rows: Row[] }[] = [];
  for (const group of Array.from(byKey.values())) {
    // one candidate per page
    const perPage = new Map<number, HfCand>();
    for (const c of group) if (!perPage.has(c.page)) perPage.set(c.page, c);
    if (perPage.size < need) continue;
    const occ = Array.from(perPage.values()).sort((a, b) => a.page - b.page);
    const groupsPerOcc = occ.map((o) => digitGroups(o.text));
    const nGroups = groupsPerOcc[0].length;
    // Classify every digit group: page number, page count, or identical text.
    const kinds: ("PAGE" | "NUMPAGES" | "LIT")[] = [];
    let ok = true;
    for (let g = 0; g < nGroups && ok; g++) {
      const vals = occ.map((o, k) => ({ v: Number(groupsPerOcc[k][g]), page: o.page + 1 }));
      const firstText = occ[0].text;
      let at = -1;
      for (let k = 0, re = /\d+/g, m: RegExpExecArray | null; (m = re.exec(firstText)); k++) if (k === g) at = m.index;
      const before = at >= 0 ? firstText.slice(0, at) : "";
      if (vals.every((x) => x.v === x.page)) kinds.push("PAGE");
      else if (vals.every((x) => x.v === n) && /(\bof|\/|\bde|\bvon|\bsur)\s*$/i.test(before)) kinds.push("NUMPAGES");
      else if (vals.every((x) => x.v === vals[0].v)) kinds.push("LIT");
      else ok = false;
    }
    if (!ok) continue;
    // Build the line from the first occurrence.
    const first = occ[0];
    let gi = 0;
    const parts: Run[][] = first.row.segs.map((s) => {
      const base = segRuns(s);
      const outRuns: Run[] = [];
      for (const r of base) {
        let last = 0;
        const re = /\d+/g;
        let m: RegExpExecArray | null;
        const pieces: Run[] = [];
        while ((m = re.exec(r.text))) {
          const kind = kinds[gi++];
          if (m.index > last) pieces.push({ ...r, text: r.text.slice(last, m.index) });
          pieces.push(kind === "LIT" ? { ...r, text: m[0] } : { ...r, text: "", field: kind });
          last = m.index + m[0].length;
        }
        if (last < r.text.length) pieces.push({ ...r, text: r.text.slice(last) });
        if (!pieces.length) pieces.push(r);
        for (const p of pieces) pushRun(outRuns, p);
      }
      return outRuns;
    });
    const row = first.row;
    const left = Math.min(...row.segs.map((s) => s.left));
    const right = Math.max(...row.segs.map((s) => s.right));
    const align: Align =
      row.segs.length > 1 ? "left" : Math.abs(left - (widthPt - right)) < 12 ? "center" : left > widthPt * 0.55 ? "right" : "left";
    made.push({ zone: first.zone, baseline: row.baseline, line: { parts, align }, rows: occ.map((o) => o.row) });
  }
  for (const m of made) for (const r of m.rows) remove.add(r);
  made.sort((a, b) => a.baseline - b.baseline);
  return {
    remove,
    header: made.filter((m) => m.zone === "header").map((m) => m.line),
    footer: made.filter((m) => m.zone === "footer").map((m) => m.line),
  };
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** A rectangle on the page in points (for pictures, which take part in the margins but not in text layout). */
export interface PageBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Margins for the Word page: where the content really starts and ends, but kept symmetrical when
 * the right side is just short lines (so centred text stays centred and text is not squeezed).
 */
export function marginsFor(
  widthPt: number,
  heightPt: number,
  text: { left: number; right: number; top: number; bottom: number } | null,
  boxes: PageBox[],
): { top: number; right: number; bottom: number; left: number } {
  const lefts = [...(text ? [text.left] : []), ...boxes.map((b) => b.left)];
  const rights = [...(text ? [text.right] : []), ...boxes.map((b) => b.right)];
  const tops = [...(text ? [text.top] : []), ...boxes.map((b) => b.top)];
  const bottoms = [...(text ? [text.bottom] : []), ...boxes.map((b) => b.bottom)];
  if (!lefts.length) return { top: 72, right: 72, bottom: 72, left: 72 };
  const left = clamp(Math.min(...lefts), 18, widthPt * 0.3);
  const rightRaw = clamp(widthPt - Math.max(...rights), 18, widthPt * 0.3);
  const right = Math.min(rightRaw, Math.max(left, 36));
  return {
    left,
    right,
    top: clamp(Math.min(...tops), 18, heightPt * 0.3),
    bottom: clamp(heightPt - Math.max(...bottoms) - 4, 18, heightPt * 0.3),
  };
}

/** Lays out a whole document. `boxes` are the pictures on each page, used only for margins. */
export function layoutDocument(contents: PageContent[], boxes: PageBox[][] = []): DocumentLayout {
  const pageRows = contents.map((c) => ({ content: c, rows: buildRows(c.items) }));
  const hf = detectHeadersFooters(
    pageRows.map((p) => ({ rows: p.rows, heightPt: p.content.heightPt })),
    contents[0]?.widthPt ?? 612,
  );
  const pages: PageLayout[] = pageRows.map(({ content, rows }, pi) => {
    const body = rows.filter((r) => !hf.remove.has(r));
    const withSegs = body.filter((r) => r.segs.length);
    const pageBounds = withSegs.length
      ? { left: Math.min(...withSegs.map(rowLeft)), right: Math.max(...withSegs.map(rowRight)) }
      : { left: 72, right: content.widthPt - 72 };
    const blocks = layoutRows(body, pageBounds);
    const text = withSegs.length
      ? { left: pageBounds.left, right: pageBounds.right, top: Math.min(...withSegs.map((r) => r.top)), bottom: Math.max(...withSegs.map((r) => r.baseline)) }
      : null;
    return {
      widthPt: content.widthPt,
      heightPt: content.heightPt,
      blocks,
      marginsPt: marginsFor(content.widthPt, content.heightPt, text, boxes[pi] ?? []),
      hasText: content.hasText,
    };
  });
  return { pages, header: hf.header, footer: hf.footer };
}

/** Plain text of a block, for previews and tests. */
export function blockText(b: LayoutBlock): string {
  if (b.kind === "paragraph") return b.runs.map((r) => (r.field ? `{${r.field}}` : r.text)).join("");
  return b.rows.map((r) => r.map((c) => c.runs.map((x) => x.text).join("")).join(" | ")).join("\n");
}
